import type React from 'react';
import { useState } from 'react';

import ArrowUpIcon from '@tradera/blueprint/theme/icons2/arrow-up.svg?jsx';
import ArrowDownIcon from '@tradera/blueprint/theme/icons2/arrow-down.svg?jsx';
import { classNames } from 'tradera/utils/class-names';
import { Button } from '../atoms/button/button';
import styles from './dropdown-menu.module.scss';

type ExpandableMenuItemProps = {
    imageUrl?: string;
    icon: React.ReactNode;
    label: React.ReactNode;
    className?: string;
    imageContainerClassName?: string;
    border: boolean;
    indentItems?: boolean;
    defaultExpanded?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
    href?: string;
    disabled?: boolean;
};

export const ExpandableMenuItem = ({
    border = true,
    imageUrl,
    icon,
    label,
    className,
    imageContainerClassName,
    indentItems,
    children,
    href,
    defaultExpanded,
    onClose,
    onOpen,
    disabled,
}: React.PropsWithChildren<ExpandableMenuItemProps>) => {
    const [expanded, setIsExpanded] = useState(defaultExpanded);

    const toggleOpen = () => {
        setIsExpanded((current) => !current);

        if (expanded) {
            onClose?.();
        } else {
            onOpen?.();
        }
    };

    return (
        <li
            className={classNames('plain-list', {
                'border-bottom': border,
            })}>
            <Button
                fluid
                noTransform
                disabled={disabled}
                theme={expanded ? 'highlight' : 'mainMenu'}
                href={href}
                onClick={(e) => {
                    if (children) {
                        e.preventDefault();
                        toggleOpen();
                    }
                }}
                aria-expanded={expanded}
                className={classNames(
                    'p-0 m-0 d-flex flex-row align-items-center focus-visible-inner',
                    className,
                )}
                brandedText>
                {icon && (
                    <span
                        className={classNames(
                            'd-flex align-items-center justify-content-center',
                            styles.itemImage,
                            imageContainerClassName,
                        )}
                        style={{ backgroundImage: imageUrl }}>
                        {icon}
                    </span>
                )}
                <span className="text-left flex-grow">{label}</span>
                {disabled ? null : (
                    <span className="ml-auto d-flex pr-2">
                        {expanded ? (
                            <ArrowUpIcon className="icon" />
                        ) : (
                            <ArrowDownIcon className="icon" />
                        )}
                    </span>
                )}
            </Button>
            {expanded && (
                <ul
                    className={classNames('plain-list', {
                        [styles.indent]: indentItems,
                    })}>
                    {children}
                </ul>
            )}
        </li>
    );
};
